export interface WaveformOptions {
  canvas: HTMLCanvasElement;
}

interface Strand {
  baseAmplitude: number;
  frequency: number;
  speed: number;
  phase: number;
  color: string;
  glowColor: string;
  lineWidth: number;
  glowBlur: number;
  alpha: number;
  verticalOffset: number;
}

export class CeruleanWaveform {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private animationId: number | null = null;
  private startTime: number = performance.now();

  private width: number = 0;
  private height: number = 0;
  private dpr: number = 1;

  // Pointer state with smooth spring-damping
  private pointer = {
    x: -9999,
    y: -9999,
    targetX: -9999,
    targetY: -9999,
    intensity: 0,
    targetIntensity: 0
  };

  // Harmonically tuned cerulean strands
  private strands: Strand[] = [
    // Background deep cerulean wash
    {
      baseAmplitude: 48,
      frequency: 0.0018,
      speed: 0.28,
      phase: 0.0,
      color: 'rgba(2, 132, 199, 0.22)',
      glowColor: 'rgba(3, 105, 161, 0.35)',
      lineWidth: 3.5,
      glowBlur: 18,
      alpha: 0.25,
      verticalOffset: 6
    },
    {
      baseAmplitude: 42,
      frequency: 0.0022,
      speed: 0.35,
      phase: 1.2,
      color: 'rgba(0, 150, 214, 0.30)',
      glowColor: 'rgba(0, 123, 167, 0.40)',
      lineWidth: 2.8,
      glowBlur: 14,
      alpha: 0.35,
      verticalOffset: -5
    },
    // Mid harmonic strands
    {
      baseAmplitude: 36,
      frequency: 0.0028,
      speed: 0.42,
      phase: 2.4,
      color: 'rgba(0, 166, 237, 0.45)',
      glowColor: 'rgba(14, 165, 233, 0.50)',
      lineWidth: 2.2,
      glowBlur: 12,
      alpha: 0.5,
      verticalOffset: 3
    },
    {
      baseAmplitude: 30,
      frequency: 0.0034,
      speed: 0.5,
      phase: 3.8,
      color: 'rgba(56, 189, 248, 0.65)',
      glowColor: 'rgba(56, 189, 248, 0.6)',
      lineWidth: 1.8,
      glowBlur: 10,
      alpha: 0.65,
      verticalOffset: -3
    },
    // Core focused cerulean ribbons
    {
      baseAmplitude: 24,
      frequency: 0.0041,
      speed: 0.62,
      phase: 4.9,
      color: 'rgba(125, 211, 252, 0.85)',
      glowColor: 'rgba(56, 189, 248, 0.85)',
      lineWidth: 1.6,
      glowBlur: 8,
      alpha: 0.85,
      verticalOffset: 1
    },
    // Luminescent central filament
    {
      baseAmplitude: 18,
      frequency: 0.0049,
      speed: 0.72,
      phase: 0.6,
      color: 'rgba(224, 242, 254, 0.95)',
      glowColor: 'rgba(186, 230, 253, 0.9)',
      lineWidth: 1.2,
      glowBlur: 6,
      alpha: 0.95,
      verticalOffset: 0
    }
  ];

  constructor(options: WaveformOptions) {
    this.canvas = options.canvas;
    const context = this.canvas.getContext('2d', { alpha: true });
    if (!context) {
      throw new Error('Canvas 2D context is not supported');
    }
    this.ctx = context;

    this.resize();
    this.attachEvents();
    this.start();
  }

  public resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);

    this.ctx.resetTransform?.();
    this.ctx.scale(this.dpr, this.dpr);
  }

  private attachEvents(): void {
    const handleMove = (clientX: number, clientY: number) => {
      const rect = this.canvas.getBoundingClientRect();
      this.pointer.targetX = clientX - rect.left;
      this.pointer.targetY = clientY - rect.top;
      this.pointer.targetIntensity = 1.0;
    };

    window.addEventListener('pointermove', (e) => {
      handleMove(e.clientX, e.clientY);
    });

    window.addEventListener('pointerleave', () => {
      this.pointer.targetIntensity = 0.0;
    });

    window.addEventListener('resize', () => {
      this.resize();
    });
  }

  public start(): void {
    if (this.animationId !== null) return;
    this.startTime = performance.now();
    this.loop();
  }

  public stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private loop = (): void => {
    this.render();
    this.animationId = requestAnimationFrame(this.loop);
  };

  private render(): void {
    const now = performance.now();
    const elapsed = (now - this.startTime) * 0.001;

    // Smooth relaxation for pointer tracking
    const lerpFactor = 0.045;
    this.pointer.x += (this.pointer.targetX - this.pointer.x) * lerpFactor;
    this.pointer.y += (this.pointer.targetY - this.pointer.y) * lerpFactor;
    this.pointer.intensity += (this.pointer.targetIntensity - this.pointer.intensity) * (lerpFactor * 0.8);

    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;
    const centerY = h * 0.52;

    // Clear frame
    ctx.clearRect(0, 0, w, h);

    // Deep ambient cerulean glow in the center
    const radial = ctx.createRadialGradient(
      w * 0.5, centerY, 10,
      w * 0.5, centerY, Math.max(w * 0.45, 320)
    );
    radial.addColorStop(0, 'rgba(0, 123, 167, 0.08)');
    radial.addColorStop(0.5, 'rgba(2, 60, 115, 0.04)');
    radial.addColorStop(1, 'rgba(2, 6, 23, 0)');
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, w, h);

    // Multi-pass additive blend for glowing cerulean ribbons
    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // Step sampling along the x axis
    const step = Math.max(3, Math.floor(w / 280));
    const totalPoints = Math.ceil(w / step) + 1;

    // Slow ambient breathing oscillation (period ~10s)
    const breath = 1.0 + 0.14 * Math.sin(elapsed * 0.62) + 0.08 * Math.cos(elapsed * 0.38);

    for (let s = 0; s < this.strands.length; s++) {
      const strand = this.strands[s];
      const time = elapsed * strand.speed;

      ctx.beginPath();

      for (let i = 0; i <= totalPoints; i++) {
        const x = i * step;

        // Tapered edge envelope so the wave is calm at borders and alive in center
        const normalizedX = x / w;
        const envelope = Math.sin(Math.PI * normalizedX);
        const windowedEnvelope = Math.pow(envelope, 0.85);

        // Multi-frequency harmonic synthesis
        const primary = Math.sin(x * strand.frequency + time + strand.phase);
        const secondary = Math.cos(x * (strand.frequency * 1.618) - time * 0.73 + strand.phase * 0.5) * 0.35;
        const tertiary = Math.sin(x * (strand.frequency * 0.54) + time * 0.41) * 0.45;

        let yOffset = (primary + secondary + tertiary) * strand.baseAmplitude * breath * windowedEnvelope;

        // Subtle interactive mouse attraction/swelling
        if (this.pointer.intensity > 0.001) {
          const dx = x - this.pointer.x;
          const dist = Math.abs(dx);
          const influenceRadius = w * 0.22;

          if (dist < influenceRadius) {
            const factor = Math.cos((dist / influenceRadius) * (Math.PI * 0.5));
            const pointerYOffset = (this.pointer.y - centerY) * 0.18;
            yOffset += pointerYOffset * factor * this.pointer.intensity * windowedEnvelope;
          }
        }

        const y = centerY + strand.verticalOffset + yOffset;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.strokeStyle = strand.color;
      ctx.lineWidth = strand.lineWidth;
      ctx.shadowColor = strand.glowColor;
      ctx.shadowBlur = strand.glowBlur;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    ctx.restore();
  }
}
